import { createRoot } from 'solid-js';
import i18n from '../../../../i18n';
import { ToastView } from './Component';
import './style.css';
export type ToastType = 'text' | 'loading' | 'success' | 'fail';
export type ToastPosition = 'top' | 'middle' | 'bottom';
export interface ShowToastOptions {
  message?: string;
  type?: ToastType;
  /** 毫秒；0 表示不自动关闭，需手动 `instance.close()` 或 `Toast.clear()` */
  duration?: number;
  position?: ToastPosition;
  /** 为 true 时拦截背后点击（类似 Vant forbidClick） */
  forbidClick?: boolean;
  /** 对齐 App showHud：黑半透卡 + 弧形转圈 / 对勾 */
  hud?: boolean;
}
export interface ToastAPI {
  handleClose: () => void;
}
let activeClose: (() => void) | null = null;
const DEFAULT_DURATION = 3000;

const defaultServiceError = () =>
  (i18n as { Common?: { ToastServiceError?: string } }).Common?.ToastServiceError ||
  '服务异常，请稍后重试';

/**
 * 从 string / Error / 业务 reject / ShowToastOptions 中取出可读文案。
 * 空值回落 i18n.Common.ToastServiceError，避免误显硬编码「服务异常」。
 */
export function resolveToastMessage(input: unknown, fallback?: string): string {
  const fb = (fallback && String(fallback).trim()) || defaultServiceError();
  if (input == null) return fb;
  if (typeof input === 'string') {
    const s = input.replace(/^\d+\s+/, '').trim();
    return s || fb;
  }
  if (typeof input === 'object') {
    const msg = (input as { message?: unknown }).message;
    if (typeof msg === 'string') {
      const s = msg.replace(/^\d+\s+/, '').trim();
      if (s) return s;
    }
  }
  return fb;
}

function normalizeOptions(options: string | ShowToastOptions): ShowToastOptions {
  return typeof options === 'string' ? { message: options } : options || {};
}
/**
 * 函数式弹出 Toast，用法接近 Vant `showToast`。
 * 挂载到 document.body（不跟 ActionSheet 挂壳），避免挂在 #h5-shell 上被框架 reconcile / iOS 键盘 resize 清掉。
 */
export function showToast(options: string | ShowToastOptions) {
  const opts = normalizeOptions(options);
  const api = {} as ToastAPI;
  // iOS：先收起键盘，减轻 fixed Toast 随 visualViewport 错位/“闪一下消失”
  (document.activeElement as HTMLElement | null)?.blur?.();
  activeClose?.();
  let toastEl: HTMLElement;
  const mount = document.body;
  createRoot((dispose) => {
    const close = () => {
      if (toastEl?.parentNode === mount) {
        mount.removeChild(toastEl);
      }
      if (activeClose === close) {
        activeClose = null;
      }
      dispose();
    };
    activeClose = close;
    toastEl = ToastView({
      message: resolveToastMessage(opts.message),
      type: opts.type ?? 'text',
      duration: opts.duration ?? DEFAULT_DURATION,
      position: opts.position ?? 'middle',
      forbidClick: opts.forbidClick ?? false,
      hud: opts.hud ?? false,
      close,
      api,
    }) as HTMLElement;
    mount.appendChild(toastEl);
  });
  return {
    close: () => api.handleClose?.(),
  };
}

/** 对齐 App showHud：黑半透卡 + 文案（非底部轻 Toast） */
export function showHud(message: string, options?: Omit<ShowToastOptions, 'message' | 'hud'>) {
  return showToast({ message, hud: true, ...options });
}

/** catch 里统一弹错误：兼容 string / Error / 拦截器 reject */
export function showToastError(err: unknown, fallback?: string) {
  return showToast(resolveToastMessage(err, fallback));
}

/** 关闭当前由本模块管理的 Toast（若有） */
export function closeToast() {
  activeClose?.();
  activeClose = null;
}
/**
 * 命名空间对象，支持 `Toast.toast('msg')`，即 `const ins = Toast; ins.toast(...)`。
 */
export const Toast = {
  toast: showToast,
  show: showToast,
  hud: showHud,
  error: showToastError,
  clear: closeToast,
};
/**
 * 若希望业务里统一 `const ins = createToast(); ins.toast(...)`，可用此工厂（行为与 Toast 等价）。
 */
export function createToast() {
  return Toast;
}
